'use client'

import { cn } from '@ui/lib/utils'
import * as React from 'react'
import * as RechartsPrimitive from 'recharts'
import type { Payload } from 'recharts/types/component/DefaultTooltipContent'

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: '', dark: '.dark' } as const

type PayloadItem = {
  dataKey?: string | number
  name?: string
  value?: number
  color?: string
  payload?: {
    fill?: string
    [key: string]: unknown
  }
}

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode
    icon?: React.ComponentType
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  )
}

type ChartContextProps = {
  config: ChartConfig
}

const ChartContext = React.createContext<ChartContextProps | null>(null)

function useChart() {
  const context = React.useContext(ChartContext)

  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />')
  }

  return context
}

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    config: ChartConfig
    children: React.ComponentProps<
      typeof RechartsPrimitive.ResponsiveContainer
    >['children']
  }
>(({ id, className, children, config, ...props }, ref) => {
  const uniqueId = React.useId()
  const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        ref={ref}
        className={cn(
          "[&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-gray-200/50 flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-gray-500 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-gray-200 [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-gray-200 [&_.recharts-radial-bar-background-sector]:fill-gray-100 [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-gray-100 [&_.recharts-reference-line_[stroke='#ccc']]:stroke-gray-200 [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none",
          className,
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer>
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  )
})
ChartContainer.displayName = 'Chart'

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(
    ([, config]) => config.theme || config.color,
  )

  if (!colorConfig.length) {
    return null
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(
            ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color =
      itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ||
      itemConfig.color
    return color ? `  --color-${key}: ${color};` : null
  })
  .join('\n')}
}
`,
          )
          .join('\n'),
      }}
    />
  )
}

const ChartTooltip = RechartsPrimitive.Tooltip

const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
    React.ComponentProps<'div'> & {
      hideLabel?: boolean
      hideIndicator?: boolean
      indicator?: 'line' | 'dot' | 'dashed'
      nameKey?: string
      labelKey?: string
      hoveredItem?: string | null
      onItemHover?: (item: string | null) => void
    }
>(
  (
    {
      active,
      payload,
      className,
      indicator = 'dot',
      hideLabel = false,
      hideIndicator = false,
      label,
      labelFormatter,
      labelClassName,
      formatter,
      color,
      nameKey,
      labelKey,
      hoveredItem,
      onItemHover,
    },
    ref,
  ) => {
    const { config } = useChart()

    const tooltipLabel = React.useMemo(() => {
      if (hideLabel || !payload?.length) {
        return null
      }

      const [item] = payload
      const key = `${labelKey || item?.dataKey || item?.name || 'value'}`
      const itemConfig = getPayloadConfigFromPayload(config, item, key)
      const value =
        !labelKey && typeof label === 'string'
          ? config[label]?.label || label
          : itemConfig?.label

      if (labelFormatter) {
        return (
          <div className={cn('font-medium', labelClassName)}>
            {labelFormatter(value, payload)}
          </div>
        )
      }

      if (!value) {
        return null
      }

      return <div className={cn('font-medium', labelClassName)}>{value}</div>
    }, [
      label,
      labelFormatter,
      payload,
      hideLabel,
      labelClassName,
      config,
      labelKey,
    ])

    if (!active || !payload?.length) {
      return null
    }

    const nestLabel = payload.length === 1 && indicator !== 'dot'
    const shouldUseColumns = payload.length > 10

    return (
      <div
        ref={ref}
        className={cn(
          'border-gray-200/50 grid min-w-[8rem] items-start gap-xxs rounded-sm border bg-white px-xs py-xxs text-xs shadow-xl',
          shouldUseColumns && 'min-w-[16rem]',
          className,
        )}
      >
        {!nestLabel ? tooltipLabel : null}
        <div
          className={cn(
            'grid gap-xxs',
            shouldUseColumns && 'grid-cols-2 gap-x-s',
          )}
        >
          {(payload as PayloadItem[]).map((item, index) => {
            const key = `${nameKey || item.name || item.dataKey || 'value'}`
            const itemConfig = getPayloadConfigFromPayload(config, item, key)
            const indicatorColor =
              color || item.payload?.fill || item.color || ''
            const isHovered = key === hoveredItem

            return (
              <div
                key={item.dataKey}
                className={cn(
                  'flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-gray-500',
                  indicator === 'dot' && 'items-center',
                  isHovered ? '' : hoveredItem ? 'opacity-40' : '',
                )}
                onMouseEnter={() => onItemHover?.(key)}
                onMouseLeave={() => onItemHover?.(null)}
              >
                {formatter &&
                item?.value !== undefined &&
                item?.payload !== undefined &&
                item.name ? (
                  formatter(
                    item.value,
                    item.name,
                    item as unknown as Payload<number, string>,
                    index,
                    [item.payload] as unknown as Payload<number, string>[],
                  )
                ) : (
                  <>
                    {itemConfig?.icon ? (
                      <itemConfig.icon />
                    ) : (
                      !hideIndicator && (
                        <div
                          className={cn(
                            'shrink-0 rounded-full border-[--color-border] bg-[--color-bg]',
                            {
                              'h-2.5 w-2.5': indicator === 'dot',
                              'w-1': indicator === 'line',
                              'w-0 border border-dashed bg-transparent':
                                indicator === 'dashed',
                              'my-0.5': nestLabel && indicator === 'dashed',
                            },
                          )}
                          style={
                            {
                              '--color-bg': indicatorColor,
                              '--color-border': indicatorColor,
                            } as React.CSSProperties
                          }
                        />
                      )
                    )}
                    <div
                      className={cn(
                        'flex flex-1 justify-between leading-none',
                        nestLabel ? 'items-end' : 'items-center',
                      )}
                    >
                      <div className="grid gap-xxs">
                        {nestLabel ? tooltipLabel : null}
                        <span
                          className={cn(
                            'text-gray-500',
                            isHovered && 'font-medium text-gray-950',
                          )}
                        >
                          {itemConfig?.label || item.name}
                        </span>
                      </div>
                      {item.value && (
                        <span
                          className={cn(
                            'font-mono font-medium tabular-nums text-gray-950',
                            isHovered && 'font-bold',
                          )}
                        >
                          {item.value.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  },
)
ChartTooltipContent.displayName = 'ChartTooltip'

const ChartLegend = RechartsPrimitive.Legend

const ChartLegendContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> &
    Pick<RechartsPrimitive.LegendProps, 'payload'> & {
      hideIcon?: boolean
      nameKey?: string
      hoveredItem?: string | null
      onItemHover?: (item: string | null) => void
    }
>(
  (
    { className, hideIcon = false, payload, nameKey, onItemHover, hoveredItem },
    ref,
  ) => {
    const { config } = useChart()
    const itemRefs = React.useRef<Record<string, HTMLDivElement | null>>({})

    React.useEffect(() => {
      if (hoveredItem && itemRefs.current[hoveredItem]) {
        const element = itemRefs.current[hoveredItem]
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }
    }, [hoveredItem])

    if (!payload?.length) {
      return null
    }

    return (
      <div
        ref={ref}
        className={cn(
          'flex h-[150px] w-full flex-wrap items-center justify-center gap-l overflow-y-auto pb-xl',
          className,
        )}
      >
        {(payload as PayloadItem[]).map((item) => {
          const key = `${nameKey || item.dataKey || 'value'}`
          const itemConfig = getPayloadConfigFromPayload(config, item, key)
          const isHovered = onItemHover ? key === hoveredItem : false

          return (
            <div
              key={item.value?.toString() || key}
              ref={(el) => {
                if (el) {
                  itemRefs.current[key] = el
                } else {
                  delete itemRefs.current[key]
                }
              }}
              className={cn(
                'group flex cursor-pointer select-none items-center gap-xs text-base transition-all duration-150',
                isHovered
                  ? 'scale-105 text-black'
                  : hoveredItem
                    ? 'text-gray-700 opacity-40'
                    : 'text-gray-700',
              )}
              onMouseEnter={() => onItemHover?.(item.dataKey as string)}
              onMouseLeave={() => onItemHover?.(null)}
            >
              {itemConfig?.icon && !hideIcon ? (
                <itemConfig.icon />
              ) : (
                <div
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{
                    backgroundColor: item.color || '',
                  }}
                />
              )}
              <span className="min-w-[max-content]">
                {itemConfig?.label || item.name}
              </span>
            </div>
          )
        })}
      </div>
    )
  },
)
ChartLegendContent.displayName = 'ChartLegend'

function getPayloadConfigFromPayload(
  config: ChartConfig,
  payload: unknown,
  key: string,
) {
  if (typeof payload !== 'object' || payload === null) {
    return undefined
  }

  const payloadPayload =
    'payload' in payload &&
    typeof payload.payload === 'object' &&
    payload.payload !== null
      ? payload.payload
      : undefined

  let configLabelKey: string = key

  if (
    key in payload &&
    typeof payload[key as keyof typeof payload] === 'string'
  ) {
    configLabelKey = payload[key as keyof typeof payload] as string
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === 'string'
  ) {
    configLabelKey = payloadPayload[
      key as keyof typeof payloadPayload
    ] as string
  }

  return configLabelKey in config ? config[configLabelKey] : config[key]
}

export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
}
